import { z } from 'zod';
import {
  TenantIdSchema,
  UlidSchema,
  RutSchema,
  EmailSchema,
  TelefonoChileSchema,
  IsoTimestampSchema,
  IsoDateSchema,
} from './common.js';

/**
 * Residente — persona que ocupa una unidad bajo un rol.
 * Un mismo individuo puede ser residente de varios condominios (multi-tenant
 * por unidad: cada link es una entidad UnidadResidente).
 */

export const RolResidenteSchema = z.enum(['propietario', 'arrendatario']);
export type RolResidente = z.infer<typeof RolResidenteSchema>;

export const ResidenteSchema = z.object({
  id: UlidSchema,
  tenantId: TenantIdSchema,
  rut: RutSchema,
  nombre: z.string().min(2).max(100),
  apellidos: z.string().min(2).max(100),
  email: EmailSchema,
  telefono: TelefonoChileSchema.optional(),
  activo: z.boolean().default(true),
  /** Set cuando el residente completa onboarding. Plan E lo usa. */
  onboardingCompletedAt: IsoTimestampSchema.optional(),
  createdAt: IsoTimestampSchema,
  updatedAt: IsoTimestampSchema,
});
export type Residente = z.infer<typeof ResidenteSchema>;

/** Vínculo residente ↔ unidad con rol, habitación y vigencia. */
export const UnidadResidenteSchema = z.object({
  tenantId: TenantIdSchema,
  unidadId: UlidSchema,
  residenteId: UlidSchema,
  rol: RolResidenteSchema,
  /**
   * Si el vínculo implica habitar la unidad.
   * - Propietario que vive: `rol=propietario, habita=true`.
   * - Propietario no habitante: `rol=propietario, habita=false`.
   * - Arrendatario: `rol=arrendatario, habita=true` típicamente.
   */
  habita: z.boolean(),
  fechaInicio: IsoDateSchema,
  fechaFin: IsoDateSchema.nullable(),
  createdAt: IsoTimestampSchema,
});
export type UnidadResidente = z.infer<typeof UnidadResidenteSchema>;

export const CreateResidenteRequestSchema = ResidenteSchema.pick({
  rut: true,
  nombre: true,
  apellidos: true,
  email: true,
  telefono: true,
}).partial({ telefono: true });
export type CreateResidenteRequest = z.infer<typeof CreateResidenteRequestSchema>;

/**
 * PATCH sobre campos mutables. RUT no es editable. El toggle de `activo`
 * NO se expone aquí — la desactivación se gestiona por `POST /residentes/{id}/desactivar`
 * (soft-delete con validación B4 de "cero asignaciones activas").
 */
export const UpdateResidenteRequestSchema = CreateResidenteRequestSchema.omit({ rut: true }).partial();
export type UpdateResidenteRequest = z.infer<typeof UpdateResidenteRequestSchema>;

export const AsignarUnidadRequestSchema = z.object({
  unidadId: UlidSchema,
  rol: RolResidenteSchema,
  habita: z.boolean(),
  fechaInicio: IsoDateSchema,
  fechaFin: IsoDateSchema.nullable().optional(),
});
export type AsignarUnidadRequest = z.infer<typeof AsignarUnidadRequestSchema>;

/** Cierra una asignación abierta. fechaFin puede ser futura (cierre programado). */
export const CerrarAsignacionRequestSchema = z.object({
  fechaFin: IsoDateSchema,
});
export type CerrarAsignacionRequest = z.infer<typeof CerrarAsignacionRequestSchema>;
